import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/** @type {WeakMap<HTMLElement, number>} */
const showTokens = new WeakMap();

/**
 * The media ids of a gallery's slides, in order.
 * @param {Element} gallery - The media gallery element.
 * @returns {string}
 */
function slideMediaIds(gallery) {
  return Array.from(gallery.querySelectorAll('slideshow-slide [data-media-id]'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.mediaId : ''))
    .join(',');
}

/**
 * Shows a media index in a freshly inserted gallery.
 *
 * The slideshow initialises asynchronously after insertion, and its start-up can reset it to
 * the first slide a frame or more later, so a single select() can be lost. For 1.5 seconds this
 * checks what is actually on screen and puts the requested slide back whenever it isn't showing.
 * It works while the gallery is scrolled out of view too (programmatic scrolling doesn't need the
 * slideshow's lazy "in-viewport" state), and stops as soon as the shopper touches the gallery so
 * it never fights a swipe or a thumbnail click.
 *
 * @param {HTMLElement} gallery - The media gallery element.
 * @param {number} index - The slide index to show.
 */
function showVariantMedia(gallery, index) {
  // A newer pick on the same gallery cancels this one, so quick clicks never fight each other.
  const token = (showTokens.get(gallery) || 0) + 1;
  showTokens.set(gallery, token);

  const deadline = performance.now() + 1500;
  let userMoved = false;
  const stop = () => {
    userMoved = true;
  };
  gallery.addEventListener('pointerdown', stop, { once: true });
  gallery.addEventListener('keydown', stop, { once: true });

  const tick = () => {
    if (userMoved || !gallery.isConnected || performance.now() > deadline) return;
    if (showTokens.get(gallery) !== token) return;

    const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (gallery.querySelector('slideshow-component'));
    const scroller = slideshow?.querySelector('slideshow-slides');
    const slide = slideshow?.querySelectorAll('slideshow-slide')[index];

    if (slideshow && scroller && slide && scroller.clientWidth > 0) {
      const slideRect = slide.getBoundingClientRect();
      const offset = slideRect.left - scroller.getBoundingClientRect().left;
      const showing = Math.abs(offset) < slideRect.width / 2;

      if (slideshow.current !== index) {
        slideshow.select(index, undefined, { animate: false });
      } else if (!showing) {
        // The slideshow already counts this slide as current, so select() would skip it.
        scroller.scrollTo({ left: scroller.scrollLeft + offset, behavior: 'instant' });
      }
    }

    setTimeout(tick, 100);
  };

  // A timer rather than requestAnimationFrame: frames pause while the page isn't painting.
  setTimeout(tick, 0);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Switch to the picked variant's photo straight away, using the images already on the page,
    // instead of waiting for Shopify to send back the re-rendered section.
    this.#showVariantImageNow(event.detail?.variantId);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const variantMediaIndex = parseInt(newMediaGallery.dataset.variantMediaIndex || '0', 10);

        // The hero-first gallery has the same photos in the same order for every variant, so keep
        // this one rather than rebuilding it (no flash, no image reload); just confirm the slide.
        if (slideMediaIds(this) === slideMediaIds(newMediaGallery)) {
          this.dataset.variantMediaIndex = String(variantMediaIndex);
          if (variantMediaIndex > 0) showVariantMedia(this, variantMediaIndex);
          return;
        }

        this.replaceWith(newMediaGallery);
        if (variantMediaIndex > 0) showVariantMedia(newMediaGallery, variantMediaIndex);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Slides to the photo of a variant, looked up in this gallery's data-variant-media map.
   * @param {string} [variantId] - The picked variant's id.
   */
  #showVariantImageNow(variantId) {
    if (!variantId) return;
    const pair = (this.dataset.variantMedia || '').split(',').find((entry) => entry.startsWith(`${variantId}:`));
    const mediaId = pair?.split(':')[1];
    if (!mediaId) return;

    const slides = Array.from(this.querySelector('slideshow-component')?.querySelectorAll('slideshow-slide') || []);
    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    if (index >= 0) showVariantMedia(this, index);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
